/**
 * Delta Capture for Excel
 *
 * Captures mini-snapshots of ONLY the cells about to be overwritten
 * before a tool executes. Uses the affected range calculator to determine
 * which cells to read.
 */

import {
  columnLetterToNumber,
  columnNumberToLetter,
  parseCellAddress,
} from '../agents/tool-utils'
import { getExcelRuntime } from '../excel-utils'
import { type CaptureStrategy, getAffectedRanges } from './affected-range'
import type { CellSnapshot, ToolDelta } from './snapshot-db'

const MAX_CELLS_PER_DELTA = 50000

/**
 * Get default cell format (fallback when getCellProperties is unavailable).
 */
function getDefaultFormat() {
  return {
    backgroundColor: null,
    fontColor: null,
    fontBold: false,
    fontItalic: false,
    fontSize: null,
    numberFormat: null,
    horizontalAlignment: null,
    verticalAlignment: null,
    wrapText: false,
  }
}

/**
 * Extract CellFormat from getCellProperties result and numberFormat value.
 */
function extractCellFormat(
  props: Record<string, unknown> | undefined,
  numberFormat: string | undefined
) {
  if (!props) {
    return {
      ...getDefaultFormat(),
      numberFormat:
        numberFormat && numberFormat !== 'General' ? numberFormat : null,
    }
  }
  const fill = props.fill as { color?: string } | undefined
  const font = props.font as
    | { bold?: boolean; italic?: boolean; color?: string; size?: number }
    | undefined
  return {
    backgroundColor: fill?.color || null,
    fontColor: font?.color || null,
    fontBold: font?.bold ?? false,
    fontItalic: font?.italic ?? false,
    fontSize: font?.size ?? null,
    numberFormat:
      numberFormat && numberFormat !== 'General' ? numberFormat : null,
    horizontalAlignment: (props.horizontalAlignment as string) ?? null,
    verticalAlignment: (props.verticalAlignment as string) ?? null,
    wrapText: (props.wrapText as boolean) ?? false,
  }
}

/**
 * Read cells from specific ranges in Excel.
 * Returns a flat record of "SheetName!CellAddress" → CellSnapshot.
 */
async function readCellsFromRanges(
  ranges: Array<{ sheetName: string | null; address: string }>
): Promise<Record<string, CellSnapshot>> {
  const cells: Record<string, CellSnapshot> = {}
  if (ranges.length === 0) return cells

  const Excel = getExcelRuntime()

  await Excel.run(async (context: Excel.RequestContext) => {
    // Resolve sheet references and load ranges in batch
    const rangeLoads: Array<{
      range: Excel.Range
      sheetName: string
    }> = []

    for (const { sheetName, address } of ranges) {
      try {
        const sheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet()

        if (!sheetName) {
          sheet.load('name')
        }

        const range = sheet.getRange(address)
        range.load([
          'values',
          'formulas',
          'numberFormat',
          'address',
          'rowCount',
          'columnCount',
        ])
        rangeLoads.push({ range, sheetName: sheetName || '' })
      } catch {
        // Range might be invalid — skip
        console.warn(
          `[DeltaCapture] Failed to load range ${sheetName || ''}!${address}`
        )
      }
    }

    if (rangeLoads.length === 0) return
    await context.sync()

    // Try to load cell properties for format data
    type CellProps = Array<Array<{ format?: Record<string, unknown> }>>
    const cellPropsMap = new Map<Excel.Range, CellProps>()
    try {
      const cellPropsLoads: Array<{
        range: Excel.Range
        load: OfficeExtension.ClientResult<Excel.CellProperties[][]>
      }> = []
      for (const { range } of rangeLoads) {
        cellPropsLoads.push({
          range,
          load: range.getCellProperties({
            format: {
              fill: { color: true },
              font: { bold: true, italic: true, color: true, size: true },
              horizontalAlignment: true,
              verticalAlignment: true,
              wrapText: true,
            },
          }),
        })
      }
      await context.sync()
      for (const { range, load } of cellPropsLoads) {
        cellPropsMap.set(range, load.value as unknown as CellProps)
      }
    } catch {
      // getCellProperties not available — fall back to default formatting
    }

    // Process loaded ranges
    let totalCells = 0
    for (const { range, sheetName: originalSheetName } of rangeLoads) {
      if (totalCells >= MAX_CELLS_PER_DELTA) break

      try {
        // Resolve actual sheet name from the range address
        const fullAddress = range.address // e.g., "Sheet1!A1:C10"
        const addressParts = fullAddress.split('!')
        const resolvedSheetName =
          addressParts.length > 1
            ? addressParts[0].replace(/^'|'$/g, '')
            : originalSheetName
        const rangeAddress =
          addressParts.length > 1 ? addressParts[1] : addressParts[0]

        const values = range.values as unknown[][]
        const formulas = range.formulas as string[][]
        const numberFormats = range.numberFormat as string[][]
        const cellProps = cellPropsMap.get(range)

        // Parse start cell
        const startAddress = rangeAddress.split(':')[0]
        const { col: startCol, row: startRow } = parseCellAddress(startAddress)
        const startColNum = columnLetterToNumber(startCol)

        for (
          let r = 0;
          r < values.length && totalCells < MAX_CELLS_PER_DELTA;
          r++
        ) {
          for (
            let c = 0;
            c < values[r].length && totalCells < MAX_CELLS_PER_DELTA;
            c++
          ) {
            const value = values[r][c]
            const formulaRaw = formulas[r][c]
            const formula =
              typeof formulaRaw === 'string' && formulaRaw.startsWith('=')
                ? formulaRaw
                : null

            const cellAddress = `${columnNumberToLetter(startColNum + c)}${startRow + r}`
            const key = `${resolvedSheetName}!${cellAddress}`

            cells[key] = {
              value,
              formula,
              format: extractCellFormat(
                cellProps?.[r]?.[c]?.format,
                numberFormats?.[r]?.[c]
              ),
            }
            totalCells++
          }
        }
      } catch {
        // Individual range read failed — continue with others
      }
    }
  })

  return cells
}

/**
 * Capture the active sheet's used range (for sheet-scan strategy).
 */
async function captureActiveSheetCells(): Promise<
  Record<string, CellSnapshot>
> {
  const cells: Record<string, CellSnapshot> = {}

  const Excel = getExcelRuntime()

  await Excel.run(async (context: Excel.RequestContext) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet()
    sheet.load('name')
    const usedRange = sheet.getUsedRangeOrNullObject()
    usedRange.load([
      'address',
      'values',
      'formulas',
      'numberFormat',
      'rowCount',
      'columnCount',
    ])
    await context.sync()

    if (!usedRange.address) return

    // Check size limit
    const totalCells = usedRange.rowCount * usedRange.columnCount
    if (totalCells > MAX_CELLS_PER_DELTA) {
      console.warn(
        `[DeltaCapture] Active sheet has ${totalCells} cells, limiting to ${MAX_CELLS_PER_DELTA}`
      )
    }

    // Try to load cell properties for format data
    type CellProps = Array<Array<{ format?: Record<string, unknown> }>>
    let cellProps: CellProps | null = null
    try {
      const propsLoad = usedRange.getCellProperties({
        format: {
          fill: { color: true },
          font: { bold: true, italic: true, color: true, size: true },
          horizontalAlignment: true,
          verticalAlignment: true,
          wrapText: true,
        },
      })
      await context.sync()
      cellProps = propsLoad.value as unknown as CellProps
    } catch {
      // getCellProperties not available — use default formatting
    }

    const sheetName = sheet.name
    const values = usedRange.values as unknown[][]
    const formulas = usedRange.formulas as string[][]
    const numberFormats = usedRange.numberFormat as string[][]

    const addressParts = usedRange.address.split('!')
    const rangeAddress = addressParts[1] || addressParts[0]
    const startAddress = rangeAddress.split(':')[0]
    const { col: startCol, row: startRow } = parseCellAddress(startAddress)
    const startColNum = columnLetterToNumber(startCol)

    let cellCount = 0
    for (let r = 0; r < values.length && cellCount < MAX_CELLS_PER_DELTA; r++) {
      for (
        let c = 0;
        c < values[r].length && cellCount < MAX_CELLS_PER_DELTA;
        c++
      ) {
        const value = values[r][c]
        const formulaRaw = formulas[r][c]
        const formula =
          typeof formulaRaw === 'string' && formulaRaw.startsWith('=')
            ? formulaRaw
            : null

        // Include all cells (even empty) for sheet-scan since we need to detect deletions
        if (value === '' && !formula) continue

        const cellAddress = `${columnNumberToLetter(startColNum + c)}${startRow + r}`
        cells[`${sheetName}!${cellAddress}`] = {
          value,
          formula,
          format: extractCellFormat(
            cellProps?.[r]?.[c]?.format,
            numberFormats?.[r]?.[c]
          ),
        }
        cellCount++
      }
    }
  })

  return cells
}

/**
 * Capture a delta before a tool executes.
 * Reads ONLY the cells that will be affected by the tool.
 */
export async function captureToolDeltaExcel(
  toolName: string,
  toolCallId: string,
  args: Record<string, unknown>
): Promise<ToolDelta | null> {
  try {
    const affected = getAffectedRanges(toolName, args)

    // No-op tools don't need delta capture
    if (affected.strategy === 'none') {
      return null
    }

    let beforeCells: Record<string, CellSnapshot>
    let strategy: CaptureStrategy = affected.strategy

    if (affected.strategy === 'targeted' && affected.ranges.length > 0) {
      // Read only the specific cells that will be overwritten
      beforeCells = await readCellsFromRanges(affected.ranges)
    } else if (affected.strategy === 'sheet-scan') {
      // Read the active sheet's used range
      beforeCells = await captureActiveSheetCells()
    } else if (affected.strategy === 'full-snapshot') {
      // For full-snapshot (e.g., deleteWorksheet), capture affected sheets
      // Fall back to active sheet capture as a reasonable approximation
      beforeCells = await captureActiveSheetCells()
      strategy = 'sheet-scan'
    } else {
      beforeCells = {}
    }

    return {
      toolCallId,
      toolName,
      timestamp: Date.now(),
      beforeCells,
      affectedRanges: affected.ranges,
      strategy,
    }
  } catch (error) {
    console.warn(
      `[DeltaCapture] Failed to capture delta for ${toolName}`,
      error
    )
    return null
  }
}
